import React, { useState, useRef, useEffect } from 'react';
import EditIcon from '@mui/icons-material/EditOutlined';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import LinkIcon from '@mui/icons-material/LinkOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { createKindBadge } from '../styles/registryStyles';
import { publishEntry, listRelations } from '../../../api/metaRegistriesApi';
import KubernetesInfrastructure from './KubernetesInfrastructure';
import KubernetesPods from './KubernetesPods';
import NamespaceDetails from './NamespaceDetails';
import EntryChildren from './EntryChildren';
import RelatedDiagrams from './RelatedDiagrams';

const PANEL_TOP = 56;

const SYNC_STATUS = {
  synced:   { bg: '#1e4d2b', color: '#4ade80', label: 'синхронизировано' },
  draft:    { bg: '#4d3a0e', color: '#fbbf24', label: 'черновик' },
  conflict: { bg: '#4d1e1e', color: '#f87171', label: 'конфликт' },
  unsynced: { bg: '#1e293b', color: '#94a3b8', label: 'не синхр.' },
};

const RegistryDetailsPanel = ({
  selected,
  kinds,
  theme,
  styles,
  onClose,
  relatedRef,
  modulesRef,
  related,
  relLoading,
  relError,
  allDiagrams,
  selectedDiagramId,
  setSelectedDiagramId,
  linking,
  linkError,
  onLinkDiagram,
  onUnlinkDiagram,
  onNavigate,
  children,
  childrenLoading,
  onChildClick,
  onDeleteChild,
  onDelete,
  onEdit,
  onAddChild,
  onLinkExisting,   // (parentEntry) => void — open link-existing picker
  onRefresh,        // () => void — refresh after publish
  registrySlug,     // string — registry slug for publish API
  projectId,        // string — project ID for publish API
  infrastructure,
  infraLoading,
  namespaceDetails,
  namespaceLoading,
  allEntries = [],
  uiProfile,
}) => {
  const [showChildMenu, setShowChildMenu] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState(null);
  const childMenuRef = useRef(null);

  // Linked systems for BC entries
  const [linkedSystems, setLinkedSystems] = useState([]);
  const [linkedSystemsLoading, setLinkedSystemsLoading] = useState(false);

  useEffect(() => {
    if (!selected || registrySlug !== 'business-capabilities') {
      setLinkedSystems([]);
      setLinkedSystemsLoading(false);
      return;
    }
    let cancelled = false;
    setLinkedSystemsLoading(true);
    listRelations(projectId, { to: selected.id })
      .then(relations => {
        if (cancelled) return;
        const systems = relations
          .filter(r => r.relation_type?.slug === 'implements')
          .map(r => r.from_entry);
        setLinkedSystems(systems.filter(Boolean));
      })
      .catch(() => { if (!cancelled) setLinkedSystems([]); })
      .finally(() => { if (!cancelled) setLinkedSystemsLoading(false); });
    return () => { cancelled = true; };
  }, [selected?.id, registrySlug, projectId]);

  const handlePublish = async () => {
    if (!selected || !registrySlug) return;
    setPublishing(true);
    setPublishError(null);
    try {
      await publishEntry(projectId, registrySlug, selected.id);
      if (onRefresh) onRefresh();
    } catch (e) {
      setPublishError(e.message || 'Ошибка публикации');
    } finally {
      setPublishing(false);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    if (!showChildMenu) return;
    const handler = (e) => {
      if (childMenuRef.current && !childMenuRef.current.contains(e.target)) {
        setShowChildMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showChildMenu]);

  const isDark = theme.name === 'dark';

  const overlayStyle = {
    position: 'fixed',
    top: PANEL_TOP,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.35)',
    transition: 'opacity .2s ease',
    opacity: selected ? 1 : 0,
    pointerEvents: selected ? 'auto' : 'none',
    zIndex: 3500
  };

  const panelStyle = {
    position: 'fixed',
    top: PANEL_TOP,
    right: 0,
    bottom: 0,
    width: 'min(520px, 90vw)',
    background: isDark ? '#0b1220' : '#ffffff',
    color: theme.toolbar.text,
    borderLeft: `1px solid ${theme.toolbar.buttonBorder}`,
    boxShadow: '0 0 0 1px rgba(0,0,0,0.02), 0 10px 30px rgba(0,0,0,0.25)',
    transform: selected ? 'translateX(0)' : 'translateX(100%)',
    transition: 'transform .25s ease',
    zIndex: 4000,
    display: 'flex',
    flexDirection: 'column'
  };

  const panelHeader = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: `1px solid ${theme.toolbar.buttonBorder}`,
    background: theme.toolbar.background,
    color: theme.toolbar.text
  };

  const iconBtn = {
    border: `1px solid ${theme.toolbar.buttonBorder}`,
    background: theme.toolbar.buttonBackground,
    color: theme.toolbar.buttonText,
    borderRadius: 6,
    padding: '6px 9px',
    cursor: 'pointer',
    fontSize: 15,
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const panelBody = {
    padding: 16,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 10
  };

  const kindBadge = selected ? createKindBadge(selected.kind_slug, kinds, theme) : {};
  const kindName = selected ? (kinds?.find(k => k.slug === selected.kind_slug)?.name || selected.kind_slug || 'Запись') : '';
  const tagNames = selected ? (selected.tags || []).map(t => t.name || t).filter(Boolean) : [];
  const infraEnabled = !!uiProfile?.features?.infrastructureDetails;

  // Top-level entries (no parent_id) show namespace-children cards instead of aggregate stats
  const isTopLevel = !selected?.parent_id;
  const nsChildren = isTopLevel && infraEnabled
    ? (children || []).filter(c => c.attributes?.k8s_resource_type === 'namespace')
    : [];
  const visibleAttributes = Object.entries(selected?.attributes || {}).filter(([key]) => (
    infraEnabled || !key.startsWith('k8s_')
  ));

  // Item 3: hide KubernetesPods when namespace details are shown
  const showKubePods = infraEnabled && !namespaceDetails && (infrastructure?.pods?.length > 0);

  return (
    <>
      <div style={overlayStyle} onClick={onClose} />
      <aside style={panelStyle} aria-hidden={!selected}>
        <div style={panelHeader}>
          <div style={{ fontWeight: 700, color: theme.toolbar.text, overflow: 'hidden' }}>
            {selected && (
              <>
                <span style={kindBadge}>{kindName}</span>
                <span style={{ marginLeft: 8 }}>
                  {selected.name} <span style={{ opacity: 0.7 }}>({selected.code})</span>
                </span>
              </>
            )}
          </div>

          {/* Icon-only action buttons */}
          <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
            {selected && registrySlug && (
              <button
                style={{
                  ...iconBtn,
                  color: publishing ? '#64748b' : '#60a5fa',
                  borderColor: publishing ? '#334155' : '#2563eb',
                  opacity: publishing ? 0.6 : 1,
                }}
                onClick={handlePublish}
                disabled={publishing}
                title={publishing ? 'Публикуется...' : 'Опубликовать в GitLab'}
              >
                <CloudUploadOutlinedIcon fontSize="small" />
              </button>
            )}
            {selected && onEdit && (
              <button style={iconBtn} onClick={() => onEdit(selected)} title="Изменить">
                <EditIcon fontSize="small" />
              </button>
            )}

            {/* Child dropdown */}
            {selected && (onAddChild || onLinkExisting) && (
              <div style={{ position: 'relative' }} ref={childMenuRef}>
                <button
                  style={iconBtn}
                  onClick={() => setShowChildMenu(v => !v)}
                  title="Добавить дочернюю"
                >
                  <AddIcon fontSize="small" />
                </button>
                {showChildMenu && (
                  <div style={{
                    position: 'absolute', top: '100%', right: 0, marginTop: 4,
                    background: isDark ? '#1a1f2e' : '#ffffff',
                    border: `1px solid ${theme.toolbar.buttonBorder}`,
                    borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                    minWidth: 210, zIndex: 6000, overflow: 'hidden',
                  }}>
                    {onAddChild && (
                      <button
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          width: '100%', textAlign: 'left',
                          padding: '10px 14px', fontSize: 13, cursor: 'pointer',
                          background: 'transparent', border: 'none',
                          color: theme.toolbar.text, borderBottom: `1px solid ${theme.toolbar.buttonBorder}`,
                        }}
                        onClick={() => { setShowChildMenu(false); onAddChild(selected); }}
                      >
                        <AddCircleOutlineIcon fontSize="small" /> Создать новую
                      </button>
                    )}
                    {onLinkExisting && (
                      <button
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          width: '100%', textAlign: 'left',
                          padding: '10px 14px', fontSize: 13, cursor: 'pointer',
                          background: 'transparent', border: 'none',
                          color: theme.toolbar.text,
                        }}
                        onClick={() => { setShowChildMenu(false); onLinkExisting(selected); }}
                      >
                        <LinkIcon fontSize="small" /> Привязать существующую
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {selected && onDelete && (
              <button
                style={{ ...iconBtn, color: '#ef4444', borderColor: '#ef4444' }}
                onClick={() => onDelete(selected.id)}
                title="Удалить"
              >
                <DeleteIcon fontSize="small" />
              </button>
            )}
            <button style={iconBtn} onClick={onClose} title="Закрыть">
              <CloseIcon fontSize="small" />
            </button>
          </div>
        </div>

        <div style={panelBody}>
          {selected && (
            <>
              {/* GitLab sync status */}
              {selected.sync_status && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {(() => {
                    const s = SYNC_STATUS[selected.sync_status] || SYNC_STATUS.unsynced;
                    return (
                      <span style={{ background: s.bg, color: s.color, borderRadius: 4, padding: '2px 8px', fontSize: 12 }}>
                        GitLab: {s.label}
                      </span>
                    );
                  })()}
                  {selected.gitlab_path && (
                    <span style={{ color: '#64748b', fontSize: 11, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {selected.gitlab_path}
                    </span>
                  )}
                </div>
              )}
              {publishError && (
                <div style={{ color: '#f87171', fontSize: 12 }}>{publishError}</div>
              )}

              <div>
                <div style={styles.fieldLabel}>Идентификатор</div>
                <div style={styles.fieldValue}>{selected.id}</div>
              </div>

              <div>
                <div style={styles.fieldLabel}>Код</div>
                <div style={styles.fieldValue}>{selected.code}</div>
              </div>

              <div>
                <div style={styles.fieldLabel}>Название</div>
                <div style={styles.fieldValue}>{selected.name}</div>
              </div>

              {selected.status && (
                <div>
                  <div style={styles.fieldLabel}>Статус</div>
                  <div style={styles.fieldValue}>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: 4,
                      fontSize: 12,
                      background: selected.status === 'active'
                        ? (isDark ? '#1e4d2b' : '#d1fae5')
                        : (isDark ? '#4a3b3b' : '#fee2e2'),
                      color: selected.status === 'active'
                        ? (isDark ? '#4ade80' : '#065f46')
                        : (isDark ? '#f87171' : '#991b1b')
                    }}>
                      {selected.status}
                    </span>
                  </div>
                </div>
              )}

              {selected.description && (
                <div>
                  <div style={styles.fieldLabel}>Описание</div>
                  <div style={styles.fieldValue}>{selected.description}</div>
                </div>
              )}

              {selected.team && (
                <div>
                  <div style={styles.fieldLabel}>Команда</div>
                  <div style={styles.fieldValue}>{selected.team.name}</div>
                </div>
              )}

              {visibleAttributes.length > 0 && (
                <div>
                  <div style={styles.fieldLabel}>Атрибуты</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
                    {visibleAttributes.map(([key, val]) => (
                      <div key={key} style={{ display: 'flex', gap: 8 }}>
                        <span style={{ ...styles.fieldLabel, minWidth: 100 }}>{key}</span>
                        <span style={styles.fieldValue}>{String(val)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {tagNames.length > 0 && (
                <div>
                  <div style={styles.fieldLabel}>Теги</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                    {tagNames.map(tag => (
                      <span key={tag} style={{
                        fontSize: 12,
                        padding: '2px 8px',
                        borderRadius: 10,
                        border: `1px solid ${theme.toolbar.buttonBorder}`,
                        color: theme.toolbar.text
                      }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selected.created_at && (
                <div>
                  <div style={styles.fieldLabel}>Создано</div>
                  <div style={styles.fieldValue}>{new Date(selected.created_at).toLocaleString()}</div>
                </div>
              )}

              {selected.updated_at && (
                <div>
                  <div style={styles.fieldLabel}>Обновлено</div>
                  <div style={styles.fieldValue}>{new Date(selected.updated_at).toLocaleString()}</div>
                </div>
              )}

              {/* Kubernetes Infrastructure — hidden only when namespace details are shown */}
              {infraEnabled && !namespaceDetails && (
                <KubernetesInfrastructure
                  infrastructure={infrastructure?.stats ?? null}
                  theme={theme}
                  borderColor={theme.toolbar.buttonBorder}
                />
              )}

              {/* Namespace cards for top-level systems */}
              {isTopLevel && nsChildren.length > 0 && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px dashed ${theme.toolbar.buttonBorder}` }}>
                  <div style={{ fontWeight: 700, marginBottom: 8, color: theme.toolbar.text }}>
                    Namespaces ({nsChildren.length})
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {nsChildren.map(ns => (
                      <div
                        key={ns.id}
                        onClick={() => onChildClick && onChildClick(ns)}
                        style={{
                          padding: '8px 10px',
                          borderRadius: 6,
                          border: `1px solid ${theme.toolbar.buttonBorder}`,
                          background: theme.name === 'dark' ? '#1a1f2e' : '#f8fafc',
                          cursor: onChildClick ? 'pointer' : 'default',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 3,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: 13, fontWeight: 600, fontFamily: 'monospace', color: theme.toolbar.text }}>
                            {ns.attributes?.k8s_namespace || ns.name}
                          </span>
                          <span style={{ fontSize: 10, opacity: 0.5, color: theme.toolbar.text }}>
                            {ns.code}
                          </span>
                        </div>
                        {ns.attributes?.k8s_cluster_id && (
                          <div style={{ fontSize: 11, opacity: 0.6, color: theme.toolbar.text, fontFamily: 'monospace' }}>
                            cluster: {ns.attributes.k8s_cluster_id}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Namespace Details — item 4: passes children for linked-system display */}
              {infraEnabled && (
                <NamespaceDetails
                  namespaceDetails={namespaceDetails}
                  loading={namespaceLoading}
                  theme={theme}
                  borderColor={theme.toolbar.buttonBorder}
                  children={children}
                  allEntries={allEntries}
                  onChildClick={onChildClick}
                />
              )}

              {/* Item 3: KubernetesPods hidden when namespace details shown */}
              {showKubePods && (
                <KubernetesPods
                  pods={infrastructure?.pods ?? null}
                  theme={theme}
                  borderColor={theme.toolbar.buttonBorder}
                  moduleCard={styles.moduleCard}
                />
              )}

              {/* Дочерние записи */}
              <EntryChildren
                selected={selected}
                children={children}
                childrenLoading={childrenLoading}
                modulesRef={modulesRef}
                theme={theme}
                styles={styles}
                kinds={kinds}
                onChildClick={onChildClick}
                onDeleteChild={onDeleteChild}
              />

              {/* Кнопки навигации */}
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button
                  style={styles.btn}
                  onClick={() => relatedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                >
                  Связанные диаграммы
                </button>
                {children && children.length > 0 && (
                  <button
                    style={styles.btn}
                    onClick={() => modulesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  >
                    Дочерние записи
                  </button>
                )}
              </div>

              {/* Связанные диаграммы */}
              <RelatedDiagrams
                relatedRef={relatedRef}
                related={related}
                relLoading={relLoading}
                relError={relError}
                allDiagrams={allDiagrams}
                selectedDiagramId={selectedDiagramId}
                setSelectedDiagramId={setSelectedDiagramId}
                linking={linking}
                linkError={linkError}
                onLinkDiagram={onLinkDiagram}
                onUnlinkDiagram={onUnlinkDiagram}
                onNavigate={onNavigate}
                theme={theme}
                styles={styles}
                entryId={selected.id}
                canLink={false}
              />

              {/* Реализующие системы — shown only for BC entries */}
              {registrySlug === 'business-capabilities' && (
                <div style={{ marginTop: 20 }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    fontWeight: 600, fontSize: 13, color: theme.toolbar.text, marginBottom: 8
                  }}>
                    <AccountTreeIcon fontSize="small" />
                    Реализующие системы
                  </div>
                  {linkedSystemsLoading ? (
                    <div style={{ fontSize: 12, opacity: 0.6, color: theme.toolbar.text }}>Загрузка...</div>
                  ) : linkedSystems.length === 0 ? (
                    <div style={{ fontSize: 12, opacity: 0.6, color: theme.toolbar.text }}>Нет привязанных систем</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {linkedSystems.map(sys => (
                        <div
                          key={sys.id}
                          style={{
                            padding: '6px 10px', borderRadius: 6, fontSize: 13,
                            background: theme.toolbar.buttonBackground,
                            color: theme.toolbar.text,
                            border: `1px solid ${theme.toolbar.buttonBorder}`,
                            cursor: onNavigate ? 'pointer' : 'default',
                          }}
                          onClick={() => onNavigate && onNavigate(sys)}
                        >
                          {sys.name}
                          {sys.code && (
                            <span style={{ fontSize: 11, opacity: 0.6, marginLeft: 6 }}>({sys.code})</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </aside>
    </>
  );
};

export default RegistryDetailsPanel;
